import { useState, useCallback, useRef } from 'react';
import type { SessionData, LoadingInfo, TracedFace, TracedObstruction } from './types';
import { MODULES, DEFAULT_MODULE } from './lib/equipment';
import { geocodeAddress } from './lib/geocode';
import { generateRoofModel } from './lib/roofModel';
import { autoPlacePanels } from './lib/panelPlacement';
import { getProductionEstimateByFace } from './lib/pvwatts';
import { fetchSatelliteImagery, type ImageryResult } from './lib/satellite';
import { extrudeTracedRoof } from './lib/roofExtrusion';
import { computeShadeAnalysis } from './lib/shadeAnalysis';
import { fetchSolarApiDesign } from './lib/solarApi';
import { createDesign, updateDesign, getDesign } from './lib/designStore';
import { Canvas3D } from './components/Canvas3D';
import { StatsPanel } from './components/StatsPanel';
import { RoofTracer } from './components/RoofTracer';
import { ContactForm } from './components/ContactForm';
import { DesignsPanel } from './components/DesignsPanel';
import './styles/global.css';

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

function App() {
  const [address, setAddress] = useState('');
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [moduleId, setModuleId] = useState(DEFAULT_MODULE.id);
  const [loading, setLoading] = useState<LoadingInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removeMode, setRemoveMode] = useState(false);
  const [shadeVisible, setShadeVisible] = useState(true);
  const [consumptionEnabled, setConsumptionEnabled] = useState(false);
  const [consumption, setConsumption] = useState<number | null>(null);
  const [imagery, setImagery] = useState<ImageryResult | null>(null);
  const [showTracer, setShowTracer] = useState(false);
  const [pendingCoords, setPendingCoords] = useState<{ lat: number; lon: number; offline: boolean } | null>(null);
  const [dataSourceNotice, setDataSourceNotice] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showDesigns, setShowDesigns] = useState(false);
  const [savedDesignId, setSavedDesignId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const snapshotRef = useRef<(() => string | null) | null>(null);

  // ============================================================
  // WORKFLOW 1: Auto Design
  // Fallback chain (all three stay available, in this order):
  //   1. Google Solar API — real roof segments + real panel layout
  //      + real production, for buildings Google has data on.
  //   2. Simulated roof — generic gable roof, used when Solar API
  //      has no coverage for this address (404) or errors out.
  // Production always comes from PVWatts when Solar API isn't used.
  // "Trace Roof" (workflow 2, below) remains available any time as
  // a manual, always-works alternative.
  // ============================================================
  const startDesign = useCallback(async () => {
    const addr = address.trim();
    if (!addr) {
      setError('Please enter an address.');
      return;
    }

    setError(null);
    setDataSourceNotice(null);
    setLoading({ state: 'geocoding', text: 'Geocoding address...', step: 'Step 1 of 4' });

    try {
      const coords = await geocodeAddress(addr);
      if (!coords) throw new Error('Could not geocode this address. Please try a more specific address.');

      // Fetch satellite imagery in parallel with roof model attempts
      setLoading({ state: 'geocoding', text: 'Loading satellite imagery...', step: 'Step 1 of 4' });
      const imageryPromise = coords.offline
        ? Promise.resolve(null)
        : fetchSatelliteImagery(coords.lat, coords.lon).catch(() => null);

      // --- Try Google Solar API first (real roof + real panels + real production) ---
      setLoading({ state: 'roof_detection', text: 'Fetching real roof data (Google Solar API)...', step: 'Step 2 of 4' });

      let roofModel;
      let panelLayout;
      let production;
      let usedGoogleSolar = false;

      const solarOutcome = coords.offline
        ? null
        : await fetchSolarApiDesign(coords.lat, coords.lon).catch(() => null);

      const imageryResult = await imageryPromise;
      setImagery(imageryResult || null);

      if (solarOutcome?.ok) {
        roofModel = solarOutcome.data.roofModel;
        panelLayout = solarOutcome.data.panelLayout;
        production = solarOutcome.data.production;
        usedGoogleSolar = true;
        setDataSourceNotice(`Real roof data from Google Solar API (${solarOutcome.data.imageryQuality.toLowerCase()} quality imagery).`);
      } else {
        // --- Fallback: simulated roof + our own panel placement + PVWatts ---
        if (solarOutcome && !solarOutcome.ok) {
          setDataSourceNotice(
            solarOutcome.reason === 'no_coverage'
              ? "Google Solar API has no data for this address — using a simulated roof instead. Try 'Trace Roof' for a shape that matches this property."
              : `Google Solar API unavailable (${solarOutcome.message}) — using a simulated roof instead.`
          );
        }
        setLoading({ state: 'roof_detection', text: 'Generating simulated roof geometry...', step: 'Step 2 of 4' });
        await sleep(300);
        roofModel = generateRoofModel();
        if (roofModel.obstructions.length > 0) {
          setLoading({ state: 'roof_detection', text: 'Computing shade analysis...', step: 'Step 2 of 4' });
          roofModel = computeShadeAnalysis(roofModel, coords.lat);
        }

        setLoading({ state: 'panel_placement', text: 'Placing solar panels...', step: 'Step 3 of 4' });
        await sleep(400);
        panelLayout = autoPlacePanels(roofModel, MODULES[moduleId]);

        setLoading({
          state: 'production',
          text: coords.offline ? 'Estimating production (offline)...' : 'Calculating production estimate (PVWatts)...',
          step: 'Step 4 of 4',
        });
        await sleep(300);
        production = await getProductionEstimateByFace(coords, roofModel, panelLayout);
      }

      if (usedGoogleSolar) {
        setLoading({ state: 'panel_placement', text: 'Applying Google\'s real panel layout...', step: 'Step 3 of 4' });
        await sleep(300);
        setLoading({ state: 'production', text: 'Using Google\'s real production estimate...', step: 'Step 4 of 4' });
        await sleep(300);
      }

      const data: SessionData = {
        address: addr,
        latitude: coords.lat,
        longitude: coords.lon,
        roof_model: roofModel,
        panel_layout: panelLayout,
        production,
        consumption: null,
      };

      setLoading({ state: 'rendering', text: 'Rendering 3D design...', step: 'Almost done...' });
      await sleep(300);
      setSessionData(data);
      setSavedDesignId(null);
      setSaveState('idle');
      setLoading(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during design generation.');
      setLoading(null);
    }
  }, [address, moduleId]);

  // ============================================================
  // WORKFLOW 2: Trace Roof mode
  // Loads imagery first, then opens the tracer for manual tracing
  // ============================================================
  const startTraceMode = useCallback(async () => {
    const addr = address.trim();
    if (!addr) {
      setError('Please enter an address.');
      return;
    }

    setError(null);
    setLoading({ state: 'geocoding', text: 'Geocoding address...', step: 'Loading imagery...' });

    try {
      const coords = await geocodeAddress(addr);
      if (!coords) throw new Error('Could not geocode this address.');

      setLoading({ state: 'geocoding', text: 'Loading satellite imagery...', step: 'Preparing tracer...' });
      const imageryResult = coords.offline
        ? null
        : await fetchSatelliteImagery(coords.lat, coords.lon).catch(() => null);

      if (!imageryResult) {
        setError('Satellite imagery is required for roof tracing. Please try again when online.');
        setLoading(null);
        return;
      }

      setImagery(imageryResult);
      setPendingCoords({ lat: coords.lat, lon: coords.lon, offline: coords.offline });
      setLoading(null);
      setShowTracer(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.');
      setLoading(null);
    }
  }, [address]);

  // ============================================================
  // Handle traced faces → extrude → place panels → production
  // ============================================================
  const handleFacesComplete = useCallback(async (tracedFaces: TracedFace[], tracedObstructions: TracedObstruction[]) => {
    setShowTracer(false);
    if (!pendingCoords || !address) return;

    setLoading({ state: 'roof_detection', text: 'Extruding roof model...', step: 'Building 3D roof...' });

    try {
      let roofModel;

      if (tracedFaces.length === 0) {
        // User chose "Use Simulated Roof"
        await sleep(300);
        roofModel = generateRoofModel();
      } else {
        roofModel = extrudeTracedRoof(tracedFaces, tracedObstructions);

        // Run shade analysis if there are obstructions
        if (tracedObstructions.length > 0) {
          setLoading({ state: 'roof_detection', text: 'Computing shade analysis...', step: 'Analyzing shadows...' });
          await sleep(200);
          roofModel = computeShadeAnalysis(roofModel, pendingCoords.lat);
        }
      }

      // Place panels
      setLoading({ state: 'panel_placement', text: 'Placing solar panels...', step: 'Step 3 of 4' });
      await sleep(400);
      const panelLayout = autoPlacePanels(roofModel, MODULES[moduleId]);

      // Production estimate
      setLoading({
        state: 'production',
        text: pendingCoords.offline ? 'Estimating production (offline)...' : 'Calculating production estimate...',
        step: 'Step 4 of 4',
      });
      await sleep(300);
      const coords = { lat: pendingCoords.lat, lon: pendingCoords.lon, display: '', offline: pendingCoords.offline };
      const production = await getProductionEstimateByFace(coords, roofModel, panelLayout);

      const data: SessionData = {
        address: address.trim(),
        latitude: pendingCoords.lat,
        longitude: pendingCoords.lon,
        roof_model: roofModel,
        panel_layout: panelLayout,
        production,
        consumption: null,
      };

      setLoading({ state: 'rendering', text: 'Rendering 3D design...', step: 'Almost done...' });
      await sleep(300);
      setSessionData(data);
      setSavedDesignId(null);
      setSaveState('idle');
      setLoading(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during extrusion.');
      setLoading(null);
    }
  }, [pendingCoords, address, moduleId]);

  const handleTracerCancel = useCallback(() => {
    setShowTracer(false);
  }, []);

  // ============================================================
  // Existing handlers (module change, consumption, panel removal, export)
  // ============================================================
  const handleModuleChange = useCallback(async (newModuleId: string) => {
    setModuleId(newModuleId);
    if (!sessionData) return;

    // If this design came from Google Solar API, its panel layout and
    // production number are Google's own real calculations tied to their
    // reference panel spec. Swapping modules switches this design over to
    // our own simplified layout algorithm + PVWatts estimate on the same
    // (real) roof shape — flag that clearly rather than silently degrading.
    if (sessionData.roof_model.source === 'google_solar') {
      setDataSourceNotice(
        "Switched to a custom panel layout on the real roof shape — production is now a PVWatts estimate rather than Google's own calculation."
      );
    }

    const newLayout = autoPlacePanels(sessionData.roof_model, MODULES[newModuleId]);
    const coords = { lat: sessionData.latitude, lon: sessionData.longitude, display: '', offline: false };
    const newProduction = await getProductionEstimateByFace(coords, sessionData.roof_model, newLayout);

    setSessionData({
      ...sessionData,
      panel_layout: newLayout,
      production: newProduction,
    });
  }, [sessionData]);

  const handleConsumptionChange = useCallback((kwh: number | null) => {
    setConsumption(kwh);
    if (!sessionData || !kwh) return;
    const offset = (sessionData.production.annual_kwh / kwh) * 100;
    setSessionData({
      ...sessionData,
      consumption: kwh,
      production: { ...sessionData.production, offset_pct: offset },
    });
  }, [sessionData]);

  const handlePanelRemoved = useCallback((panelId: string) => {
    if (!sessionData) return;
    const newPanels = sessionData.panel_layout.panels.filter(p => p.id !== panelId);
    const newCount = newPanels.length;
    const newKW = (newCount * sessionData.panel_layout.equipment.module.wattage) / 1000;
    setSessionData({
      ...sessionData,
      panel_layout: {
        ...sessionData.panel_layout,
        panels: newPanels,
        total_count: newCount,
        total_system_kw: newKW,
      },
    });
  }, [sessionData]);

  // ============================================================
  // D1 persistence: save current design / load a saved design
  // ============================================================
  const handleSaveDesign = useCallback(async () => {
    if (!sessionData) return;
    setSaveState('saving');
    try {
      const thumbnail = snapshotRef.current?.() ?? null;
      if (savedDesignId) {
        await updateDesign(savedDesignId, sessionData, moduleId, thumbnail);
      } else {
        const id = await createDesign(sessionData, moduleId, sessionData.address, thumbnail);
        setSavedDesignId(id);
      }
      setSaveState('saved');
    } catch (err) {
      console.error('Save design failed:', err);
      setSaveState('error');
    }
  }, [sessionData, savedDesignId, moduleId]);

  const handleLoadDesign = useCallback(async (id: string) => {
    setShowDesigns(false);
    setError(null);
    setDataSourceNotice(null);
    setLoading({ state: 'rendering', text: 'Loading saved design...', step: 'Fetching from database...' });

    try {
      const design = await getDesign(id);

      // Re-fetch satellite imagery from the saved coordinates (not persisted in D1)
      setLoading({ state: 'rendering', text: 'Loading satellite imagery...', step: 'Almost done...' });
      const imageryResult = await fetchSatelliteImagery(design.latitude, design.longitude).catch(() => null);
      setImagery(imageryResult);

      setAddress(design.address);
      setModuleId(design.module_id);
      setConsumption(design.consumption);
      setConsumptionEnabled(design.consumption !== null);
      setSessionData({
        address: design.address,
        latitude: design.latitude,
        longitude: design.longitude,
        roof_model: design.roof_model,
        panel_layout: design.panel_layout,
        production: design.production,
        consumption: design.consumption,
      });
      setSavedDesignId(design.id);
      setSaveState('idle');
      setLoading(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load design.');
      setLoading(null);
    }
  }, []);

  const handleExport = useCallback(() => {
    if (!sessionData) return;
    const blob = new Blob([JSON.stringify(sessionData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `heliodesign_${sessionData.latitude.toFixed(4)}_${sessionData.longitude.toFixed(4)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sessionData]);

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="logo">
          <svg className="logo-icon" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.2" y1="4.2" x2="5.6" y2="5.6" /><line x1="18.4" y1="18.4" x2="19.8" y2="19.8" />
            <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.2" y1="19.8" x2="5.6" y2="18.4" /><line x1="18.4" y1="5.6" x2="19.8" y2="4.2" />
          </svg>
          HelioDesign
          <span className="header-tag">Solar Design Tool — Prototype</span>
        </div>
        <div className="address-bar">
          <input
            type="text"
            className="address-input"
            placeholder="Enter a property address (e.g., 1600 Amphitheatre Pkwy, Mountain View, CA)"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && startDesign()}
          />
          <button
            className="btn-design"
            onClick={startDesign}
            disabled={loading !== null || showTracer}
          >
            {loading ? 'Designing...' : '⚡ Auto Design'}
          </button>
          <button
            className="btn-trace"
            onClick={startTraceMode}
            disabled={loading !== null || showTracer}
            style={{
              padding: '10px 20px',
              border: '1px solid #f59e0b',
              borderRadius: 8,
              background: 'transparent',
              color: '#f59e0b',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            🏗️ Trace Roof
          </button>
          <button
            className="btn-trace"
            onClick={() => setShowDesigns(true)}
            disabled={loading !== null || showTracer}
            style={{
              padding: '10px 20px',
              border: '1px solid #334155',
              borderRadius: 8,
              background: 'transparent',
              color: '#94a3b8',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            📂 My Designs
          </button>
          <button
            className="btn-contact"
            onClick={() => setShowContactForm(true)}
          >
            ✉️ Contact
          </button>
        </div>
      </div>

      {showContactForm && <ContactForm onClose={() => setShowContactForm(false)} />}
      {showDesigns && <DesignsPanel onLoad={handleLoadDesign} onClose={() => setShowDesigns(false)} />}

      {/* Main */}
      <div className="main">
        {/* Roof Tracer overlay */}
        {showTracer && imagery && (
          <RoofTracer
            imagery={imagery}
            onFacesComplete={handleFacesComplete}
            onCancel={handleTracerCancel}
          />
        )}

        <Canvas3D
          data={sessionData}
          moduleId={moduleId}
          removeMode={removeMode}
          shadeVisible={shadeVisible}
          imagery={imagery}
          onPanelRemoved={handlePanelRemoved}
          snapshotRef={snapshotRef}
        />

        {/* Overlays inside canvas area */}
        {!sessionData && !loading && !showTracer && (
          <div className="empty-state" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <h2>Enter an address to begin</h2>
            <p>Type a property address above and click "Auto Design" or "Trace Roof".</p>
          </div>
        )}

        {loading && (
          <div className="loading-overlay" style={{ position: 'absolute' }}>
            <div className="loading-content">
              <div className="loading-spinner" />
              <div className="loading-text">{loading.text}</div>
              <div className="loading-step">{loading.step}</div>
            </div>
          </div>
        )}

        {error && (
          <div className="error-msg" style={{ position: 'absolute' }}>
            {error}
          </div>
        )}

        {!error && dataSourceNotice && sessionData && !loading && (
          <div className="data-source-notice" style={{ position: 'absolute' }}>
            {dataSourceNotice}
            <button
              className="data-source-notice-dismiss"
              onClick={() => setDataSourceNotice(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        <div id="info-tooltip" className="info-tooltip" />

        {sessionData && (
          <StatsPanel
            data={sessionData}
            moduleId={moduleId}
            onModuleChange={handleModuleChange}
            shadeVisible={shadeVisible}
            onToggleShade={setShadeVisible}
            onToggleRemoveMode={() => setRemoveMode(!removeMode)}
            removeMode={removeMode}
            onExport={handleExport}
            onConsumptionChange={handleConsumptionChange}
            consumptionEnabled={consumptionEnabled}
            onToggleConsumption={(enabled) => {
              setConsumptionEnabled(enabled);
              if (!enabled) setConsumption(null);
            }}
            onSave={handleSaveDesign}
            saveState={saveState}
          />
        )}
      </div>
    </>
  );
}

export default App;
